<template>
  <transition name="overlay-fade">
    <div
      v-show="isVisible"
      class="overlay"
      role="dialog"
      aria-modal="true"
      @click.self="closeModal"
    >
      <transition name="card-zoom">
        <div v-show="isVisible" class="card" ref="card">
          <!-- Header -->
          <header class="card-head">
            <div class="title">
              <i class="fa-solid fa-qrcode"></i>
              <span v-if="newQrCode">Nový QR</span>
              <span v-else>QR kód</span>
            </div>

            <div class="head-actions">
              <button
                v-if="!newQrCode"
                class="icon danger"
                @click="deleteqr()"
                title="Zmazať"
                aria-label="Zmazať"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
              <button
                class="icon"
                @click="closeModal"
                title="Zavrieť"
                aria-label="Zavrieť"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </header>

          <!-- Body -->
          <section class="card-body">
            <!-- CREATE MODE -->
            <form v-if="newQrCode" class="form" @submit.prevent="save">
              <div class="grid2">
                <div class="field">
                  <label for="surname">Priezvisko</label>
                  <input
                    id="surname"
                    type="text"
                    placeholder="napr. Novák"
                    v-model.trim="qr.surname"
                    autocomplete="family-name"
                  />
                </div>

                <div class="field">
                  <label for="name">Meno</label>
                  <input
                    id="name"
                    type="text"
                    placeholder="napr. Ján"
                    v-model.trim="qr.name"
                    autocomplete="given-name"
                  />
                </div>
              </div>

              <div class="field">
                <label for="email">Email</label>
                <input
                  id="email"
                  type="email"
                  placeholder="napr. jan.novak@domena.sk"
                  v-model.trim="qr.email"
                  @input="touch('email')"
                  :class="{ invalid: touched.email && !validEmail }"
                  autocomplete="email"
                  required
                />
                <p v-if="touched.email && !validEmail" class="hint error">
                  Zadajte platný email.
                </p>
              </div>

              <div class="grid2">
                <div class="field">
                  <label>Odkedy</label>
                  <VueDatePicker
                    v-model="qr.start_from"
                    placeholder="Odkedy"
                    :enable-time-picker="false"
                    format="MM/dd/yyyy"
                    :clearable="true"
                    @update:model-value="touch('start_from')"
                  />
                  <p v-if="touched.start_from && !qr.start_from" class="hint error">
                    Dátum je povinný.
                  </p>
                </div>

                <div class="field">
                  <label>Dokedy</label>
                  <VueDatePicker
                    v-model="qr.valid_until"
                    placeholder="Dokedy"
                    :enable-time-picker="false"
                    format="MM/dd/yyyy"
                    :min-date="qr.start_from || undefined"
                    :clearable="true"
                    @update:model-value="touch('valid_until')"
                  />
                  <p v-if="touched.valid_until && !qr.valid_until" class="hint error">
                    Dátum je povinný.
                  </p>
                  <p v-else-if="dateOrderInvalid" class="hint error">
                    „Dokedy“ musí byť v ten istý alebo neskorší deň ako „Odkedy“.
                  </p>
                </div>
              </div>
            </form>

            <!-- VIEW MODE -->
            <div v-else class="preview">
              <div class="qr-wrap">
                <qr-code :text="qr.qrcode_value" :options="{ width: 280 }" />
              </div>

              <div class="meta">
                <div class="kv" v-if="qr.surname || qr.name">
                  <span class="k">Meno</span>
                  <span class="v">{{ [qr.surname, qr.name].filter(Boolean).join(' ') }}</span>
                </div>
                <div class="kv">
                  <span class="k">Email</span>
                  <span class="v mono">{{ qr.email }}</span>
                </div>
                <div class="kv">
                  <span class="k">Odkedy</span>
                  <span class="v">{{ formatLocal(qr.start_from) }}</span>
                </div>
                <div class="kv">
                  <span class="k">Dokedy</span>
                  <span class="v">{{ formatLocal(qr.valid_until) }}</span>
                </div>

                <div class="kv row-copy">
                  <span class="k">Hodnota</span>
                  <div class="copy">
                    <input class="mono" :value="qr.qrcode_value" readonly />
                    <button class="btn ghost small" @click="copyValue">
                      <i class="fa-regular fa-copy"></i> Kopírovať
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Footer -->
          <footer class="card-foot">
            <button class="btn ghost" @click="closeModal">Zrušiť</button>

            <button
              v-if="newQrCode"
              class="btn"
              :disabled="!formValid || isSaving"
              @click="save"
            >
              <i class="fa-solid fa-floppy-disk" v-if="!isSaving"></i>
              <i class="fa-solid fa-spinner spin" v-else></i>
              <span>{{ isSaving ? 'Ukladám…' : 'Uložiť' }}</span>
            </button>

            <button v-else class="btn" @click="closeModal">Hotovo</button>
          </footer>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import ClientService from '@/services/client'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import QrCode from '../QrCode.vue'

export default {
  name: 'QrModal',
  components: { VueDatePicker, QrCode },
  data(){
    return {
      isVisible:false,
      newQrCode:false,
      isSaving:false,
      qr:{},
      touched:{ email:false, start_from:false, valid_until:false }
    }
  },
  computed:{
    validEmail(){
      if(!this.qr?.email) return false
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.qr.email)
    },
    dateOrderInvalid(){
      const s=this.qr?.start_from ? new Date(this.qr.start_from) : null
      const e=this.qr?.valid_until ? new Date(this.qr.valid_until) : null
      if(!s || !e) return false
      const sv=new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime()
      const ev=new Date(e.getFullYear(), e.getMonth(), e.getDate()).getTime()
      return ev < sv
    },
    formValid(){
      return this.validEmail && this.qr.start_from && this.qr.valid_until && !this.dateOrderInvalid
    }
  },
  methods:{
    openModal(qr){
      this.qr = structuredClone(qr || {})
      this.newQrCode = !this.qr.id
      this.isVisible = true
      this.isSaving = false
      this.touched = { email:false, start_from:false, valid_until:false }
      this.$nextTick(()=> {
        const el = this.$refs.card?.querySelector('input,button,[tabindex]:not([tabindex="-1"])')
        el && el.focus()
      })
      window.addEventListener('keydown', this.onKey)
    },
    closeModal(){
      this.isVisible=false
      window.removeEventListener('keydown', this.onKey)
    },
    onKey(e){
      if(e.key === 'Escape') this.closeModal()
      const isMac = navigator.platform.toUpperCase().includes('MAC')
      if(this.newQrCode && (isMac ? e.metaKey : e.ctrlKey) && e.key === 'Enter' && this.formValid){
        e.preventDefault()
        this.save()
      }
    },
    touch(f){ this.touched[f] = true },
    formatDate(date){
      if (!date) return null
      const d = new Date(date)
      const local = new Date(d.getTime() - d.getTimezoneOffset()*60000)
      return local.toISOString().split('T')[0]
    },
    formatLocal(d){
      if(!d) return ''
      const x=new Date(d)
      const y=x.getFullYear(), m=String(x.getMonth()+1).padStart(2,'0'), dd=String(x.getDate()).padStart(2,'0')
      return `${y}-${m}-${dd}`
    },
    async save(){
      if(!this.formValid || this.isSaving) return
      this.isSaving = true

      // create payload; trim optional name/surname
      const payload = {
        ...this.qr,
        name: (this.qr.name || '').trim() || undefined,
        surname: (this.qr.surname || '').trim() || undefined
      }
      if (payload.start_from) payload.start_from = this.formatDate(payload.start_from)
      if (payload.valid_until) payload.valid_until = this.formatDate(payload.valid_until)

      try{
        await ClientService.createQrCode(payload)
        toast('QR úspešne vytvorený', { autoClose: 4000 })
        this.$emit('success')
        this.closeModal()
      }catch(err){
        const e = err?.response?.data
        if (e?.errors) {
          Object.values(e.errors).forEach(msg =>
            toast(msg, { autoClose: 5000 })
          )
        } else {
          toast(e?.message || 'Nepodarilo sa uložiť QR', { autoClose: 5000 })
        }
      }finally{
        this.isSaving = false
      }
    },
    async deleteqr(){
      if(!this.qr?.id) return
      const ok = confirm('Naozaj chcete zmazať tento QR?')
      if(!ok) return
      try{
        await ClientService.deleteQrCode(this.qr.id)
        toast('QR úspešne zmazaný', { autoClose: 4000 })
        this.$emit('success')
        this.closeModal()
      }catch{
        toast('Mazanie zlyhalo', { autoClose: 4000 })
      }
    },
    copyValue(){
      if(!this.qr?.qrcode_value) return
      navigator.clipboard.writeText(this.qr.qrcode_value)
        .then(()=> toast('Skopírované do schránky', { autoClose: 2500 }))
        .catch(()=> toast('Kopírovanie zlyhalo', { autoClose: 3000 }))
    }
  }
}
</script>

<style scoped>
/* transitions */
.overlay-fade-enter-active, .overlay-fade-leave-active{ transition: opacity .15s ease; }
.overlay-fade-enter-from, .overlay-fade-leave-to{ opacity:0; }
.card-zoom-enter-active, .card-zoom-leave-active{ transition: transform .18s ease, opacity .18s ease; }
.card-zoom-enter-from, .card-zoom-leave-to{ transform: scale(.98); opacity:0; }

/* overlay */
.overlay{
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,.45);
  display: grid; place-items: center;
  padding: 20px;
}

/* card (auto width; responsive cap) */
.card{
  background: #ffffff;
  width: auto;
  max-width: 90vw;
  border-radius: 14px;
  border: 1px solid #e6ebf2;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
  display: flex; flex-direction: column;
}
@media (max-width:640px){ .card{ max-width: 95vw; } }

.card-head{
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 16px; border-bottom:1px solid #e6ebf2;
}
.title{
  display:flex; align-items:center; gap:10px; font-weight:800; font-size:18px; color:#0a2a47;
}
.title i{ color:#369af1; }
.head-actions{ display:flex; gap:8px; }
.icon{
  border:1px solid #e6ebf2; background:#ffffff; padding:8px 10px; border-radius:10px; cursor:pointer;
}
.icon:hover{ background:#f5f8ff; }
.icon.danger{ color:#ef4444; border-color:#f3d1d1; }
.icon.danger:hover{ background:#fde8e8; }

.card-body{ padding:16px; }
.card-foot{
  padding:12px 16px; display:flex; justify-content:flex-end; gap:10px; border-top:1px solid #e6ebf2;
}

/* form */
.form{ display:flex; flex-direction:column; gap:14px; }
.grid2{ display:grid; grid-template-columns: 1fr 1fr; gap:14px; }
@media (max-width:640px){ .grid2{ grid-template-columns:1fr; } }

.field{ display:flex; flex-direction:column; gap:6px; }
label{ font-weight:700; color:#0b2540; }
input[type="email"], input[type="text"]{
  width:calc(100% - 24px); padding:10px 12px; border-radius:10px; border:1px solid #e6ebf2;
  font-size:14px;
}
.invalid{ border-color:#f2b1b1; background:#fff6f6; }
.hint{ font-size:12px; color:#64748b; }
.hint.error{ color:#b91c1c; }

/* preview */
.preview{ display:flex; gap:16px; align-items:flex-start; }
.qr-wrap{
  flex:0 0 auto; background:#f8fbff; border:1px dashed #e6ebf2;
  padding:14px; border-radius:12px;
}
.meta{ display:flex; flex-direction:column; gap:8px; flex:1; }
.kv{ display:grid; grid-template-columns:120px 1fr; gap:8px; align-items:center; }
.k{ color:#64748b; font-weight:600; }
.v{ color:#0f172a; }
.row-copy .copy{ display:flex; gap:8px; }
.row-copy input{ flex:1; padding:10px 12px; border-radius:10px; border:1px solid #e6ebf2; }
.mono{ font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono','Courier New', monospace; }

/* buttons */
.btn{
  display:inline-flex; align-items:center; gap:8px;
  padding:10px 14px; border-radius:10px;
  border:1px solid #369af1; background:#369af1; color:#ffffff; font-weight:700;
  cursor:pointer;
}
.btn.ghost{ background:#ffffff; color:#369af1; }
.btn.small{ padding:8px 10px; font-size:13px; }
.btn:disabled{ opacity:.6; cursor:not-allowed; }
.spin{ animation:spin 1s linear infinite; }
@keyframes spin{ to{ transform:rotate(360deg); } }
</style>
